import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BackIcon, BlossomMark } from '@/components/icons/Icons'
import { useCopy } from '@/i18n/useCopy'
import styles from './Layout.module.css'

interface StageProps {
  children: ReactNode
  /** The bottom control rail. */
  rail?: ReactNode
  actions?: ReactNode
  /**
   * "split" puts the orb in the upper half with captions and controls below,
   * which is what a live conversation needs. "centered" makes the whole thing
   * one stack, for the welcome and ended moments.
   */
  layout?: 'split' | 'centered'
  /** Suppresses the wordmark in the header when the screen already shows it. */
  showWordmark?: boolean
}

/** The fixed, always-dark surface a conversation happens on. */
export function Stage({
  children,
  rail,
  actions,
  layout = 'split',
  showWordmark = true,
}: StageProps) {
  const copy = useCopy()

  return (
    <div className={styles.stage}>
      <header className={styles.stageHeader}>
        <span className={styles.mark}>
          {showWordmark ? (
            <>
              <BlossomMark size={22} />
              <span className={styles.markName}>{copy.ui.appName}</span>
            </>
          ) : null}
        </span>
        {actions ? <div className={styles.headerActions}>{actions}</div> : <span />}
      </header>

      <div className={styles.stageBody} data-layout={layout}>
        {children}
      </div>

      {rail ?? null}
    </div>
  )
}

export function OrbSlot({ children }: { children: ReactNode }) {
  return <div className={styles.orbSlot}>{children}</div>
}

export function BelowOrb({ children }: { children: ReactNode }) {
  return <div className={styles.belowOrb}>{children}</div>
}

export function StageIconLink({
  to,
  label,
  children,
}: {
  to: string
  label: string
  children: ReactNode
}) {
  return (
    <Link to={to} className={styles.iconLink} aria-label={label}>
      {children}
    </Link>
  )
}

interface PaperProps {
  title: string
  children: ReactNode
  /** Where the back control goes. Defaults to browser history. */
  backTo?: string
  action?: ReactNode
}

/** The scrolling, colour-scheme-following surface for reading and reviewing. */
export function Paper({ title, children, backTo, action }: PaperProps) {
  const copy = useCopy()
  const navigate = useNavigate()

  return (
    <div className={styles.paper}>
      <header className={styles.paperHeader}>
        <button
          type="button"
          className={styles.paperBack}
          onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
          aria-label={copy.ui.control.back}
        >
          <BackIcon size={20} />
        </button>
        <h1 className={styles.paperTitle}>{title}</h1>
        {action ?? <span />}
      </header>
      <main className={styles.paperBody}>{children}</main>
    </div>
  )
}

export function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      {title ? <h2 className={styles.sectionTitle}>{title}</h2> : null}
      {children}
    </section>
  )
}

export function Lead({ children }: { children: ReactNode }) {
  return <p className={styles.lead}>{children}</p>
}
